"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/news_update_headline.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/news_update_headline.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/news_update_headline.png-wvc-sizes"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/community_action_event.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/community_action_event.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/community_action_event.png-wvc-sizes"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/organization_activity_report.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/organization_activity_report.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/organization_activity_report.png-wvc-sizes"
}];
const fallbackCategories = ["BERITA TERKINI", "LAPORAN KEGIATAN", "OPINI PUBLIK"];
function formatDate(dateString?: string): string {
  if (!dateString) return "—";
  try {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, "0");
    const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    const month = months[d.getMonth()];
    const year = String(d.getFullYear());
    return `${day}.${month}.${year}`;
  } catch {
    return "—";
  }
}
function calculateReadTime(content?: string): string {
  if (!content) return "5 MIN BACA";
  const words = content.replace(/<[^>]*>/g, "").split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} MIN BACA`;
}
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}
function ReportsGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="border border-border bg-card rounded-md overflow-hidden">
            <div className="aspect-[16/10] bg-muted animate-pulse" />
            <div className="p-8 space-y-4">
              <div className="h-3 w-24 bg-muted animate-pulse rounded-md" />
              <div className="h-6 w-full bg-muted animate-pulse rounded-md" />
              <div className="h-4 w-3/4 bg-muted animate-pulse rounded-md" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-border bg-card rounded-md p-8 text-center">
        <p className="text-muted-foreground font-mono text-sm uppercase tracking-wider">
          Gagal memuat berita
        </p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="border border-border bg-card rounded-md p-8 text-center">
        <p className="text-muted-foreground font-mono text-sm uppercase tracking-wider">
          Belum ada berita tersedia
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.slice(0, 3).map((post, i) => {
      const fallback = fallbackImages[i % fallbackImages.length];
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name.toUpperCase() : fallbackCategories[i % fallbackCategories.length];
      const excerpt = stripHtml(post.excerpt);
      const readTime = calculateReadTime(post.content);
      return <article key={post.id} data-index={i} className="group relative flex flex-col border border-border bg-card rounded-md overflow-hidden transition-all duration-300 hover:border-primary/40">
            <Link to={post.link || "#"} className="block">
              <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                <img src={post.featuredImage || fallback.src} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-primary/0 transition-colors duration-300 group-hover:bg-primary/15" />
              </div>
            </Link>

            <div className="flex flex-1 flex-col p-8">
              <div className="font-mono text-xs uppercase tracking-widest text-primary">
                {category}
              </div>

              <Link to={post.link || "#"} className="mt-4 block">
                <h3 className="font-default text-[22px] leading-tight text-foreground line-clamp-3 transition-colors duration-300 group-hover:text-primary" style={{
              fontFamily: "var(--font-serif)"
            }}>
                  {post.title}
                </h3>
              </Link>

              {excerpt && <p className="mt-4 text-sm leading-relaxed text-muted-foreground line-clamp-2">
                  {excerpt}
                </p>}

              <div className="mt-auto pt-6">
                <div className="h-px w-full bg-border" />
                <div className="mt-4 flex items-center justify-between font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  <span>{formatDate(post.date)}</span>
                  <span>{readTime}</span>
                </div>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function Pagination() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="4556" id="pagination" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-16">
          <div className="flex-1">
            <div className="font-mono text-xs uppercase tracking-widest text-primary mb-4">
              06 / BERITA TERBARU
            </div>
            <h2 className="font-default text-4xl sm:text-5xl md:text-[56px] leading-[1.05] text-foreground" style={{
            fontFamily: "var(--font-serif)"
          }}>
              Informasi terkini dari organisasi.
            </h2>
          </div>

          <div className="md:flex-shrink-0">
            <Link to="/NewsPage">
              <Button variant="outline" className="font-mono text-xs uppercase tracking-widest border-border bg-transparent text-foreground hover:bg-primary/10 hover:border-primary/60 hover:text-foreground rounded-md px-5 h-10">
                Lihat Semua Berita
                <ArrowRight className="ml-1 size-4" />
              </Button>
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <ReportsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}